import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../api/axios';
import { useAuth } from './AuthContext';

// Story 17 — polls GET /api/subscription/status so every page under DashboardLayout can
// show the access-state banner and disable gated actions. 5 min poll: this state changes
// rarely (a payment webhook or a manual DB flip), so no need for the dashboard's 60s cadence.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/subscription/status');
      setStatus(res.data);
    } catch {
      // Silent — a failed status fetch just means no banner renders this cycle; it must
      // never block the rest of the app from working.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus(null);
      setLoading(false);
      return undefined;
    }

    fetchStatus();
    const intervalId = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, fetchStatus]);

  const isGated = status?.state === 'READ_ONLY' || status?.state === 'DORMANT';

  const value = useMemo(
    () => ({ status, loading, isGated, refetch: fetchStatus }),
    [status, loading, isGated, fetchStatus]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
